import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRowWrite, type RowEndpoint } from '@signalvote/data';
import type { RowVersion, TriageItem } from '@signalvote/shared';
import {
  optimisticallyPatchSortInboxRow,
  reconcileSortInboxRow,
  rollbackSortInbox,
  rowMutationScope,
  type SortInboxSnapshot,
} from './sortInboxOptimistic';
import { transientRetry } from './ledgerActionRetry';

/** The token each flippable row carries: a bank line's is still a plain number. */
interface FlipToken {
  Contribution: RowVersion;
  Expenditure: RowVersion;
  BankTransaction: number;
}

export type FlippableEntity = keyof FlipToken;

interface FlippableRow<E extends FlippableEntity> {
  matches: (id: string) => (item: TriageItem) => boolean;
  /** The sort-inbox item with the row's token replaced. */
  withVersion: (item: TriageItem, version: FlipToken[E]) => TriageItem;
}

const flippableRows: { [E in FlippableEntity]: FlippableRow<E> } = {
  Contribution: {
    matches: (id) => (item) => item.kind === 'Contribution' && item.contribution.id === id,
    withVersion: (item, version) =>
      item.kind === 'Contribution' ? { ...item, contribution: { ...item.contribution, concurrencyVersion: version } } : item,
  },
  Expenditure: {
    matches: (id) => (item) => item.kind === 'Expenditure' && item.expenditure.id === id,
    withVersion: (item, version) =>
      item.kind === 'Expenditure' ? { ...item, expenditure: { ...item.expenditure, concurrencyVersion: version } } : item,
  },
  BankTransaction: {
    matches: (id) => (item) => item.kind === 'BankLine' && item.bt.id === id,
    withVersion: (item, version) => (item.kind === 'BankLine' ? { ...item, bt: { ...item.bt, concurrencyVersion: version } } : item),
  },
};

export interface OptimisticStatusFlipConfig<E extends FlippableEntity, B, R extends { concurrencyVersion: FlipToken[E] }> {
  /**
   * Shares a TanStack scope with a bank line's classify, which is not a row write yet, so a
   * reopen and an immediate classify on one line still queue.
   */
  scopeId?: string;
  endpoint: RowEndpoint<E, FlipToken[E], B, R>;
  /** The `mark*` patch mirroring the resolved projection the settle refetch produces. */
  patch: (item: TriageItem) => TriageItem;
  /** The hook's own `onSettled` list/derived invalidation set. */
  invalidateKeys: (queryClient: QueryClient, id: string) => void;
}

/**
 * A ledger status flip: a row write that patches the sort inbox on click, rolls back on error
 * and invalidates its keys when it settles. The token and the per-row queue are
 * {@link useRowWrite}'s. The returned token is also written into the cached row, because some
 * writes to the same rows still read their token from there: a bank line's classify, a
 * contribution's contributor-shape fix and the resolution dialog's actions.
 */
export function useOptimisticStatusFlip<E extends FlippableEntity, B, R extends { concurrencyVersion: FlipToken[E] }>(
  config: OptimisticStatusFlipConfig<E, B, R>,
) {
  const queryClient = useQueryClient();
  const { endpoint, patch, invalidateKeys, scopeId } = config;
  const rows: FlippableRow<E> = flippableRows[endpoint.entity];

  return useRowWrite<E, FlipToken[E], B, R, { previous: SortInboxSnapshot }>(endpoint, {
    scope: rowMutationScope(scopeId),
    // Transient network blips auto-retry (bounded); a terminal server answer
    // surfaces at once so its rollback + inline Retry fire immediately.
    ...transientRetry,
    onMutate: async ({ row }) => ({
      previous: await optimisticallyPatchSortInboxRow(queryClient, rows.matches(row.id), patch),
    }),
    onError: (_error, _input, context) => {
      rollbackSortInbox(queryClient, context?.previous);
    },
    onSuccess: (written, { row }) => {
      reconcileSortInboxRow(queryClient, rows.matches(row.id), (item) => rows.withVersion(item, written.concurrencyVersion));
    },
    onSettled: (_data, _error, { row }) => {
      invalidateKeys(queryClient, row.id);
    },
  });
}
